import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import instance from "../../Interceptor/api_instance";
import { PageLayout } from "../../Components/PageLayout";
import { TablePlaceHolder } from "../../Components/LoadingAnimations/LoadingAnimations";
import SelectedCell from "./SelectedCell";
import CompletedReports from "../../Components/Graphics/CompletedReports";

const TaskBoardView = () => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [grouped, setGrouped] = useState([]); // [{ assigned_mechanic, tasksByStatus: { status: [tasks] } }]

  // single source-of-truth statuses for grouping and rendering
  const statuses = ["pending", "in-progress"];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // keep API request parameters exactly as they are
        const response = await instance.get(
          "/safety/maintenance-tasks/?status=pending&status=in-progress"
        );
        const tasks = response.data?.results || response.data || [];

        // Group tasks by mechanic id (or "unassigned") and by statuses
        const mechMap = new Map();
        tasks.forEach((task) => {
          const mech = task.assigned_mechanic || {
            id: "unassigned",
            full_name: t("Unassigned"),
          };
          const key = mech.id ?? mech.full_name;
          if (!mechMap.has(key)) {
            // initialize all status buckets to ensure consistent columns
            const tasksByStatus = {};
            statuses.forEach((s) => (tasksByStatus[s] = []));
            mechMap.set(key, { assigned_mechanic: mech, tasksByStatus });
          }
          const entry = mechMap.get(key);
          const st = (task.status || "").toLowerCase();
          if (statuses.includes(st)) {
            entry.tasksByStatus[st].push(task);
          }
        });

        // convert to array and sort by mechanic name
        const groupedArray = Array.from(mechMap.values()).sort((a, b) => {
          const an = (a.assigned_mechanic.full_name || "").toLowerCase();
          const bn = (b.assigned_mechanic.full_name || "").toLowerCase();
          return an < bn ? -1 : an > bn ? 1 : 0;
        });
        setGrouped(groupedArray);
      } catch (err) {
        console.error("Error fetching tasks:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  return (
    <PageLayout title={"Task Board"}>
      {selectedCell && (
        <SelectedCell close={() => setSelectedCell(null)} mechanic={selectedCell.mechanic} status={selectedCell.status} tasks={selectedCell.tasks} />
      )}
      <div className="overview-header">
        <h1 className="global-h1">{t("page-headers.tasks")}</h1>
      </div>
      <div className="graphics-cont">
        <CompletedReports />
      </div>
      {error && <p className="error">{error}</p>}
      {loading ? (
        <TablePlaceHolder />
      ) : (
        <>
          <div className="table-cont">
            <table id="forwardTable" className="yard-table">
              <thead>
                <tr className="table-header">
                  <th>{t("yard-tables.table-columns.name")}</th>
                  {statuses.map((s) => (
                    <th key={s}>
                      {t(`forms.type.maintenance-report.status-options.${s}`) ||
                        s}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grouped.map((g, i) => (
                  <tr
                    key={`mechanic-${i}`}
                    className="data-rows"
                    data-testid={`row-mechanic-${i}`}
                  >
                    <td className="">{g.assigned_mechanic.full_name}</td>
                    {statuses.map((s) => (
                      <td
                        key={s}
                        className="gray row-hover"
                        onClick={() =>
                          setSelectedCell({
                            mechanic: g.assigned_mechanic.full_name,
                            status: s,
                            tasks: g.tasksByStatus?.[s] || [],
                          })
                        }
                        data-testid={`cell-${g.assigned_mechanic.id}-${s}`}
                      >
                        {Array.isArray(g.tasksByStatus?.[s])
                          ? g.tasksByStatus[s].length
                          : 0}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageLayout>
  );
};

export default TaskBoardView;
